// Tipo CSA Dashboard
export interface CSACliente {
  id: string;
  nome: string;
}

export interface SeverityInfo {
  color: string;
  priority: number;
  label: string;
}

export interface RiscoGeral {
  nivel: string;
  cor: string;
}

export interface EstatisticasGerais {
  totalVulnerabilidades: number;
  hostsUnicos: number;
  cvesUnicas: number;
  scoreMediaCVSS: string;
}

export interface AnalisePorSeveridade {
  severity: string;
  count: number;
  color: string;
  priority: number;
  label: string;
  percentual: number;
}

export interface TopVulnerabilidade {
  cve: string;
  count: number;
}

export interface AnaliseTemporal {
  periodo: string;
  count: number;
}

export interface HostVulneravel {
  host: string;
  vulnerabilidades: number;
}

export interface DistribuicaoScoreCVSS {
  alto: number;
  medio: number;
  baixo: number;
  informativo: number;
}

export interface DashboardCSAData {
  cliente: CSACliente;
  estatisticasGerais: EstatisticasGerais;
  analisePorSeveridade: AnalisePorSeveridade[];
  topVulnerabilidades: TopVulnerabilidade[];
  analiseTemporal: AnaliseTemporal[];
  hostsVulneraveis: HostVulneravel[];
  scoreCVSS: DistribuicaoScoreCVSS;
  ultimaAtualizacao: string | null;
  totalResultados: number;
}

export interface VulnerabilidadeCSA {
  _id: string;
  id: string;
  cliente_id: string;
  anexo_id: string;
  created_at: string;
  updated_at: string;
  resume: string;
  suggestionsForCorrection: string;
  bestPractices: string;
  host: string;
  ip: string;
  port: string;
  severity: string;
  cve: string;
  cwe: string;
  cvssMetrics: string;
  cvssScore: string;
  tags: string;
  reference: string;
  severityInfo: SeverityInfo;
  riscoGeral: RiscoGeral;
  dataFormatada: string;
  tempoDecorrido: string;
}

export interface PaginacaoCSA {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface VulnerabilidadesResponse {
  vulnerabilidades: VulnerabilidadeCSA[];
  paginacao: PaginacaoCSA;
  filtros?: {
    severity?: string;
    host?: string;
    search?: string;
    cve?: string;
    ip?: string;
    port?: string;
    minCvssScore?: number;
    sortBy?: string;
    sortOrder?: string;
  };
}

export interface FiltrosVulnerabilidades {
  page?: number;
  limit?: number;
  severity?: string;
  host?: string;
  search?: string;
  cve?: string;
  ip?: string;
  port?: string;
  minCvssScore?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface HealthCheckResponse {
  status: string;
  service: string;
  timestamp: string;
  version: string;
  environment: string;
}

export interface CSAError {
  message: string;
  error?: string;
  stack?: string;
  debug?: string;
}

// Enums para valores constantes
export enum SeverityLevel {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  INFO = 'info'
}

export enum SortFields {
  CREATED_AT = 'created_at',
  UPDATED_AT = 'updated_at',
  SEVERITY = 'severity',
  HOST = 'host',
  CVSS_SCORE = 'cvssScore',
  CVE = 'cve'
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc'
}

// Types para formulários e filtros
export interface CSAFilterForm {
  severity: string;
  host: string;
  search: string;
  cve?: string;
  ip?: string;
  port?: string;
  minCvssScore?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface CSASortConfig {
  field: SortFields;
  order: SortOrder;
}

// Types para componentes de UI
export interface CSATableColumn {
  key: string;
  title: string;
  sortable?: boolean;
  width?: string;
  render?: (value: any, record: VulnerabilidadeCSA) => React.ReactNode;
}

export interface CSAChartData {
  name: string;
  value: number;
  color?: string;
}

export interface CSAMetrics {
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'stable';
  color?: string;
  icon?: string;
}

// Types para exportação de dados
export interface CSAExportOptions {
  format: 'csv' | 'xlsx' | 'pdf';
  includeDetails: boolean;
  filtros?: FiltrosVulnerabilidades;
}

export interface CSAReportData {
  cliente: CSACliente;
  dataGeracao: string;
  periodo: {
    inicio: string;
    fim: string;
  };
  resumoExecutivo: {
    totalVulnerabilidades: number;
    riscoGeral: string;
    recomendacoesPrioritarias: string[];
  };
  vulnerabilidades: VulnerabilidadeCSA[];
}

// API Response types
export interface APIResponse<T> {
  data?: T;
  message?: string;
  error?: CSAError;
  success: boolean;
}

// Types específicos para geração de relatórios PDF
export interface RelatorioOptions {
  periodo: {
    inicio: string;
    fim: string;
  };
  severidades: string[];
  incluirDetalhes: boolean;
  incluirGraficos: boolean;
  incluirRecomendacoes: boolean;
  incluirResumoExecutivo: boolean;
  incluirIndice: boolean;
  incluirEstatisticas: boolean;
  formato: 'pdf';
  filtros: {
    host?: string;
    cvssMinimo?: number;
    cveEspecifico?: string;
    portaEspecifica?: string;
    limiteDetalhes?: number;
  };
}

export interface CSAReportStats {
  totalVulnerabilidades: number;
  vulnerabilidadesPorSeveridade: Record<string, number>;
  hostsMaisAfetados: Array<{ host: string; count: number }>;
  periodo: {
    inicio: string;
    fim: string;
    dias: number;
  };
}

// Types para validação de filtros
export interface FiltroValidacao {
  campo: string;
  valor: any;
  erro?: string;
  valido: boolean;
}

export interface ResultadoValidacaoFiltros {
  valido: boolean;
  erros: string[];
  filtrosLimpos: FiltrosVulnerabilidades;
}

// Types para estatísticas filtradas
export interface EstatisticasFiltradas {
  original: EstatisticasGerais;
  filtrada: EstatisticasGerais;
  diferencaPercentual: {
    vulnerabilidades: number;
    hosts: number;
    cves: number;
    scoreCVSS: number;
  };
}

// Types para comparação de dados
export interface ComparacaoCSA {
  dadosOriginais: DashboardCSAData;
  dadosFiltrados: DashboardCSAData;
  filtrosAplicados: string[];
  impactoFiltros: {
    vulnerabilidadesRemovidas: number;
    percentualMantido: number;
    hostsAfetados: number;
    cvesAfetadas: number;
  };
}

// Types para busca avançada
export interface BuscaAvancadaCSA {
  termo: string;
  campos: Array<keyof VulnerabilidadeCSA>;
  caseSensitive: boolean;
  regexEnabled: boolean;
  resultados: {
    vulnerabilidades: VulnerabilidadeCSA[];
    totalEncontrado: number;
    tempoResposta: number;
  };
}

// Types para relatórios customizados
export interface RelatorioCustomizado {
  nome: string;
  descricao?: string;
  filtros: FiltrosVulnerabilidades;
  opcoes: RelatorioOptions;
  agendamento?: {
    ativo: boolean;
    frequencia: 'diario' | 'semanal' | 'mensal';
    proximaExecucao: string;
  };
  historico: Array<{
    dataGeracao: string;
    tamanhoArquivo: number;
    tempoProcessamento: number;
    status: 'sucesso' | 'erro';
  }>;
}

// Types para métricas de performance
export interface MetricasPerformanceCSA {
  tempoCarregamentoDashboard: number;
  tempoCarregamentoVulnerabilidades: number;
  tempoGeracaoRelatorio: number;
  quantidadeDadosProcessados: number;
  eficienciaFiltros: {
    filtro: string;
    tempoExecucao: number;
    registrosAfetados: number;
  }[];
}

// Types para auditoria e logs
export interface AuditoriaCSA {
  usuario: string;
  acao: 'visualizacao' | 'filtro' | 'relatorio' | 'busca';
  timestamp: string;
  parametros: Record<string, any>;
  resultado: {
    sucesso: boolean;
    tempoExecucao: number;
    registrosAfetados: number;
  };
}

// Types para cache e otimização
export interface CacheCSA {
  chave: string;
  dados: any;
  timestamp: string;
  expiresAt: string;
  tamanho: number;
  hitCount: number;
}

// Types para normalização de dados
export interface DadosNormalizados {
  vulnerabilidade: VulnerabilidadeCSA;
  cveNormalizado: string;
  hostNormalizado: string;
  severidadeNormalizada: SeverityLevel;
  scoreNormalizado: number;
  metadados: {
    normalizacaoAplicada: string[];
    versaoNormalizacao: string;
  };
}

// Types para webhooks e notificações
export interface NotificacaoCSA {
  tipo: 'nova_vulnerabilidade' | 'alta_severidade' | 'relatorio_concluido';
  cliente: CSACliente;
  dados: {
    vulnerabilidade?: VulnerabilidadeCSA;
    relatorio?: {
      nome: string;
      url: string;
      tamanho: number;
    };
  };
  configuracao: {
    webhook?: string;
    email?: string[];
    canal?: string;
  };
}

// Types consolidados de resposta
export type DashboardStatsResponse = APIResponse<DashboardCSAData>;
export type VulnerabilidadesListResponse = APIResponse<VulnerabilidadesResponse>;
export type VulnerabilidadeDetailsResponse = APIResponse<VulnerabilidadeCSA>;
export type HealthResponse = APIResponse<HealthCheckResponse>;
export type RelatorioResponse = APIResponse<{
  url: string;
  fileName: string;
  size: number;
  generatedAt: string;
}>;

// Types para contexto e estado global
export interface CSAContextState {
  cliente: CSACliente | null;
  dashboardData: DashboardCSAData | null;
  filtrosAtivos: FiltrosVulnerabilidades;
  vulnerabilidadesSelecionadas: string[];
  configuracoes: {
    autoRefresh: boolean;
    intervalRefresh: number;
    notificacoes: boolean;
  };
  ui: {
    loading: boolean;
    erro: string | null;
    modalAberto: boolean;
    modoVisualizacao: 'tabela' | 'cards' | 'lista';
  };
}

// Types para ações do reducer
export type CSAAction = 
  | { type: 'SET_CLIENTE'; payload: CSACliente }
  | { type: 'SET_DASHBOARD_DATA'; payload: DashboardCSAData }
  | { type: 'SET_FILTROS'; payload: FiltrosVulnerabilidades }
  | { type: 'ADICIONAR_VULNERABILIDADE_SELECIONADA'; payload: string }
  | { type: 'REMOVER_VULNERABILIDADE_SELECIONADA'; payload: string }
  | { type: 'LIMPAR_VULNERABILIDADES_SELECIONADAS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERRO'; payload: string | null }
  | { type: 'TOGGLE_MODAL' }
  | { type: 'SET_MODO_VISUALIZACAO'; payload: 'tabela' | 'cards' | 'lista' };

// Constantes úteis
export const SEVERITY_COLORS = {
  [SeverityLevel.HIGH]: '#ff4d4f',
  [SeverityLevel.MEDIUM]: '#faad14',
  [SeverityLevel.LOW]: '#52c41a',
  [SeverityLevel.INFO]: '#1890ff'
} as const;

export const SEVERITY_LABELS = {
  [SeverityLevel.HIGH]: 'Alta',
  [SeverityLevel.MEDIUM]: 'Média',
  [SeverityLevel.LOW]: 'Baixa',
  [SeverityLevel.INFO]: 'Informativa'
} as const;

export const CVSS_RANGES = {
  CRITICO: { min: 9.0, max: 10.0, color: '#722ed1' },
  ALTO: { min: 7.0, max: 8.9, color: '#ff4d4f' },
  MEDIO: { min: 4.0, max: 6.9, color: '#faad14' },
  BAIXO: { min: 0.1, max: 3.9, color: '#52c41a' },
  INFORMATIVO: { min: 0.0, max: 0.0, color: '#1890ff' }
} as const;

export const SORT_DIRECTIONS = {
  ASC: 'asc' as const,
  DESC: 'desc' as const
};

export const DEFAULT_PAGINATION = {
  page: 1,
  limit: 10,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number, range: [number, number]) => 
    `${range[0]}-${range[1]} de ${total} vulnerabilidades`
} as const;

export const FILTROS_PADRAO: FiltrosVulnerabilidades = {
  page: 1,
  limit: 10,
  sortBy: 'created_at',
  sortOrder: 'desc'
} as const;